"use client";

import React from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { DropdownMenuItemProps } from './types';

export function DropdownMenuItem({
  id: _id, // Exclude from props spread (used only for React key in parent)
  label,
  icon,
  subtitle,
  onClick,
  disabled = false,
  hasChevron = false,
  count,
  badge,
  rightElement,
  selected = false,
  variant = 'default',
  className,
  onMouseEnter,
  onMouseLeave,
  'aria-label': ariaLabel,
  ...props
}: DropdownMenuItemProps) {
  const handleClick = (e: React.MouseEvent) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    onClick?.(e);
  };

  const isDestructive = variant === 'destructive';
  // Icons sit at --text-sec, except on rows that deliberately carry their own
  // colour (destructive, or an accent action like "New page") — there the icon
  // inherits the row colour instead of going grey against a coloured label.
  // Size/alignment utilities (text-xs, text-left, …) are not colours, so they
  // must not trigger the inherit path.
  const NON_COLOR_TEXT_UTIL = /^(xs|sm|base|lg|\d?xl|left|center|right|justify|start|end|wrap|nowrap|balance|pretty|ellipsis|clip)$/;
  const setsOwnTextColor = (className ?? "")
    .split(/\s+/)
    .some((c) => {
      const suffix = /^!?(?:[a-z-]+:)*text-(.+)$/.exec(c)?.[1];
      return suffix !== undefined && !NON_COLOR_TEXT_UTIL.test(suffix);
    });
  const iconInheritsRowColor = isDestructive || setsOwnTextColor;

  // Variant styles. Guided: destructive rows read red at rest so a whole class
  // of action is legible before the label is, not only once hovered.
  const variantStyles = {
    default: "text-gray-700 dark:text-gray-300 hover-component ",
    destructive: "text-[#cf3a3f] dark:text-[#f0787c] hover:bg-[color-mix(in_srgb,#e5484d,transparent_90%)]",
  };

  // Selected state styles (overrides variant for selected items)
  const selectedStyles = selected && variant === 'default'
    ? "bg-selected"
    : "";

  return (
    <button
      type="button"
      data-menu-item-id={_id}
      onClick={handleClick}
      disabled={disabled}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      aria-label={ariaLabel || label}
      className={cn(
        // Guided row primitive: 38px target, 7/10px padding, 7px radius.
        "flex justify-between w-full min-h-[38px] px-2.5 py-[7px] text-sm rounded-[7px]",
        "transition-colors focus:outline-none",
        // Top-align once a description is present so the icon lines up with the
        // label line; otherwise centre the single line in the taller target.
        subtitle ? "items-start" : "items-center",
        // Selected state (overrides variant for selected items)
        selectedStyles || variantStyles[variant],
        // Disabled state
        disabled && "opacity-[0.45] cursor-not-allowed",
        className
      )}
      {...props}
    >
      {/* Left side: Icon + Label (with optional description stacked below). */}
      <div className={cn("flex gap-2 min-w-0 flex-1", subtitle ? "items-start" : "items-center")}>
        {icon && (
          <span
            aria-hidden="true"
            className={cn(
              "flex-shrink-0 flex items-center",
              subtitle && "mt-px",
              !iconInheritsRowColor && "text-sec",
            )}
          >
            {icon}
          </span>
        )}
        <div className="min-w-0 flex-1 flex flex-col items-start text-left">
          <span className="truncate max-w-full">{label}</span>
          {subtitle && (
            <span className="truncate max-w-full text-xs leading-[1.3] text-faint mt-0.5">
              {subtitle}
            </span>
          )}
        </div>
      </div>

      {/* Right side: value pill / badge + ChevronRight + Custom rightElement */}
      <div className={cn("flex items-center gap-1.5 text-faint flex-shrink-0 min-w-0", subtitle && "mt-px")}>
        {count !== undefined && count !== null && (
          <span className="text-xs rounded-[5px] bg-[var(--hover-bg)] px-[7px] py-0.5 truncate max-w-[120px] text-gray-700 dark:text-gray-300">
            {count}
          </span>
        )}
        {badge && (
          <span className="text-xs rounded-[5px] bg-[var(--hover-bg)] px-[7px] py-0.5 truncate max-w-[120px] text-gray-700 dark:text-gray-300">
            {badge}
          </span>
        )}
        {rightElement && (
          <span className="text-sm">{rightElement}</span>
        )}
        {hasChevron && (
          <ChevronRight className="h-[15px] w-[15px] text-faint" aria-hidden="true" />
        )}
      </div>
    </button>
  );
}
